import { createContext, useState, useEffect, ReactNode, Dispatch, SetStateAction } from "react";

type AlertSeverity = 'success' | 'info' | 'warning' | 'error';
type VerticalPosition = 'top' | 'bottom';
type HorizontalPosition = 'left' | 'center' | 'right';
export type ThemeMode = 'light' | 'dark';

interface AlertState {
    open: boolean;
    vertical: VerticalPosition;
    horizontal: HorizontalPosition;
    message: string;
    severity: AlertSeverity;
    autoHideDuration: number | null;
    action: ReactNode | null;
}

interface Alert {
    title: string;
    message: string;
    severity: AlertSeverity;
}

interface GlobalContextType {
    authenticated: boolean;
    setAuthenticated: Dispatch<SetStateAction<boolean>>;
    openLeft: boolean;
    handlerSideLeft: (bool: boolean) => void;
    openRight: boolean;
    handlerSideRight: (bool: boolean, pathname?: string) => void;
    handlerAlert: (bool: boolean, alert: Alert) => void;
    openAlert: boolean;
    setOpenAlert: Dispatch<SetStateAction<boolean>>;
    alert: Alert;
    setAlert: Dispatch<SetStateAction<Alert>>;
    themeMode: ThemeMode;
    toggleThemes: () => void;
    openBottom: boolean;
    handlerSideBottom: (bool: boolean) => void;
    hide: boolean;
    collapse: boolean;
    setCollapse: Dispatch<SetStateAction<boolean>>;
    hidden: () => void;
    alertState: AlertState;
    setAlertState: Dispatch<SetStateAction<AlertState>>;
}

const initialAlertState: AlertState = {
    open: false,
    vertical: 'top',
    horizontal: 'center',
    message: 'I love snacks',
    severity: 'info',
    autoHideDuration: null,
    action: null
};

const alerta: Alert = {
    title: 'title',
    message: 'message',
    severity: 'success'
};

const GlobalContext = createContext<GlobalContextType>({
    authenticated: true,
    setAuthenticated: () => { },
    openLeft: false,
    handlerSideLeft: () => { },
    openRight: false,
    handlerSideRight: () => { },
    handlerAlert: () => { },
    openAlert: false,
    setOpenAlert: () => { },
    alert: alerta,
    setAlert: () => { },
    themeMode: 'light',
    toggleThemes: () => { },
    openBottom: false,
    handlerSideBottom: () => { },
    hide: false,
    collapse: false,
    setCollapse: () => { },
    hidden: () => { },
    alertState: initialAlertState,
    setAlertState: () => { }
});

interface GlobalProviderProps {
    children: ReactNode;
}

const GlobalProvider: React.FC<GlobalProviderProps> = ({ children }) => {
    const [authenticated, setAuthenticated] = useState<boolean>(true);
    const [openLeft, setOpenLeft] = useState<boolean>(localStorage.getItem('openLeft') === 'true');
    const [openRight, setOpenRight] = useState<boolean>(localStorage.getItem(`right-${window.location.pathname}`) === 'true');
    const [openBottom, setOpenBottom] = useState<boolean>(localStorage.getItem('openBottom') === 'true');
    const [openAlert, setOpenAlert] = useState<boolean>(false);
    const [hide, setHide] = useState<boolean>(localStorage.getItem('hide') === 'false');
    const [collapse, setCollapse] = useState<boolean>(localStorage.getItem('collapse') === 'true');
    const [alert, setAlert] = useState<Alert>({
        title: 'title',
        message: 'message',
        severity: 'warning',
    });

    const [alertState, setAlertState] = useState<AlertState>(initialAlertState);

    const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme === 'dark' ? 'dark' : 'light';
    });

    const handlerSideLeft = (bool: boolean): void => {
        localStorage.setItem('openLeft', bool.toString());
        setOpenLeft(bool);
    };

    const handlerSideRight = (bool: boolean): void => {
        localStorage.setItem(`right-${window.location.pathname}`, bool.toString());
        setOpenRight(bool);
    };

    const handlerSideBottom = (bool: boolean): void => {
        localStorage.setItem('openBottom', bool.toString());
        setOpenBottom(bool);
    };

    const handlerAlert = (bool: boolean, alert: Alert): void => {
        setAlert(alert);
        setOpenAlert(bool);
    };

    const toggleThemes = (): void => {
        const newThemeMode = themeMode === 'light' ? 'dark' : 'light';
        localStorage.setItem('theme', newThemeMode);
        setThemeMode(newThemeMode);
    };

    const hidden = (): void => {
        if (hide === false) {
            setCollapse(false);
            setHide(true);
        } else {
            setCollapse(true);
            setHide(false);
        }
    };

    useEffect(() => {
        setAuthenticated(sessionStorage.getItem('authenticated') === 'true');
    }, []);

    return (
        <GlobalContext.Provider
            value={{
                authenticated,
                setAuthenticated,
                openLeft,
                handlerSideLeft,
                openRight,
                handlerSideRight,
                handlerAlert,
                openAlert,
                setOpenAlert,
                alert,
                setAlert,
                themeMode,
                toggleThemes,
                openBottom,
                handlerSideBottom,
                hide,
                collapse,
                setCollapse,
                hidden,
                alertState,
                setAlertState
            }}
        >
            {children}
        </GlobalContext.Provider>
    );
};

export { GlobalContext, GlobalProvider };
;
